import { FC, useEffect, useRef, useState, MouseEvent } from 'react';
import { IoClose } from 'react-icons/io5';
import axios, { AxiosResponse } from 'axios';
import { Col, Row, Select, SelectProps, Tooltip, Typography } from 'antd';
import type { RefSelectProps } from 'antd/es/select';
import type { TooltipPlacement } from 'antd/es/tooltip';
import UserAvatar from 'components/UserAvatar';
import UserAvatarPopover from 'components/UserAvatarPopover';
import apiRoutes from 'config/apiRoute';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import apiRequests from 'utils/api';
import { debounceAsync } from 'utils/debounce';
import styles from './index.module.less';

type SelectUserAvatarOptions = Required<SelectProps<any>>['options'];

const { Text } = Typography;

interface SelectUserAvatarProps {
  value?: string | string[];
  placeholder?: string;
  defaultParams?: Record<string, any>;
  defaultOptions?: SelectUserAvatarOptions;
  project?: any;
  projectId?: any;
  mode?: SelectProps<any>['mode'];
  size?: SelectProps<any>['size'];
  colorLabel?: 'default' | 'secondary';
  filterType?: ('regular_user' | 'contact' | 'freelancer')[];
  filters?: any;
  bordered?: boolean;
  onlyAvatar?: boolean;
  disabled?: boolean;
  filterRender?: (values: SelectUserAvatarOptions) => SelectUserAvatarOptions;
  dropdownMatchSelectWidth?: boolean | number;
  onChange?: (value: any, option: any) => void;
  style?: React.CSSProperties;
  mentionables?: boolean;
  planInput?: boolean;
  avatarPopover?: boolean;
  avatarPopoverPlacement?: TooltipPlacement;
}

const debounceFetchFactory = () => {
  return debounceAsync<AxiosResponse>((url: string, params: any) => {
    return apiRequests.get(url, params);
  }, 300);
};

const SelectUserAvatar: FC<SelectUserAvatarProps> = ({
  value,
  placeholder,
  defaultParams,
  defaultOptions,
  project,
  projectId,
  mode,
  size = 'large',
  colorLabel,
  filterType,
  filters,
  bordered = false,
  onlyAvatar = false,
  disabled,
  filterRender,
  dropdownMatchSelectWidth,
  onChange,
  style,
  mentionables = false,
  planInput = false,
  avatarPopover = false,
  avatarPopoverPlacement = 'leftTop',
}) => {
  const [options, setOptions] = useState<SelectUserAvatarOptions>(defaultOptions ?? []);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectRef = useRef<RefSelectProps>(null);
  const debounceGetOptionsRef = useRef<ReturnType<typeof debounceFetchFactory>>();

  if (!debounceGetOptionsRef.current) {
    debounceGetOptionsRef.current = debounceFetchFactory();
  }

  const fetchUsers = async (search = '') => {
    if (!project && !projectId) return;

    if (!debounceGetOptionsRef.current) return;

    setLoading(true);

    const url = mentionables ? `${apiRoutes.BASE_URL}/projects/${projectId ?? project.uuid}/mentionables` : apiRoutes.CONTACTS;

    try {
      const { data } = await debounceGetOptionsRef.current(url, {
        'page[size]': 50,
        ...(filters ?? {}),
        ...defaultParams,
        search_term: search ?? undefined,
      });

      const source = mentionables
        ? data.data.filter((item: any) => item.type !== 'group')
        : data.data.filter((item: any) => item.user);

      const mapped = source.map((item: any) =>
        mentionables
          ? { value: item.uuid, label: item.name, user: item }
          : { value: item.user.uuid, label: item.user.name, user: item.user },
      );

      const newOptions = mapped.filter(
        (item: any) => !defaultOptions || !defaultOptions.find((x: any) => x.value === item.value),
      );

      setOptions([...(defaultOptions ?? []), ...newOptions]);
    } catch (error) {
      if (!axios.isCancel(error)) {
        asyncErrorHandler(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const tagRender: SelectProps<any>['tagRender'] = (props) => {
    const { value: propValue, onClose } = props;
    const onPreventMouseDown = (event: MouseEvent<any>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const option = [...(defaultOptions ?? []), ...options].find((item) => item.value === propValue);

    return (
      <div className={styles.avatarContainer}>
        {!disabled && (
          <Tooltip title={`Remove ${option?.label}`} placement="top">
            <button type="button" onMouseDown={onPreventMouseDown} onClick={onClose} className={styles.removeUser}>
              <IoClose />
            </button>
          </Tooltip>
        )}

        <Tooltip title={option?.label} placement="top">
          <UserAvatar size="small" user={option?.user} />
        </Tooltip>
      </div>
    );
  };

  // The selected value reuses the option content, so the user card is only attached
  // while the dropdown is closed. That keeps it on the selected avatar and off the
  // options being browsed inside the dropdown.
  // The job title is hidden because the options come from the mentionables endpoint,
  // which returns the user type (`regular_user`) in that field instead of a real title.
  const showAvatarPopover = avatarPopover && !dropdownOpen;

  useEffect(() => {
    setOptions((prev) => {
      const merged = [...(defaultOptions ?? []), ...prev];
      const unique = merged.filter((item, index, arr) => arr.findIndex((x) => x.value === item.value) === index);
      return unique;
    });

    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, projectId]);

  return (
    <Select
      ref={selectRef}
      mode={mode}
      loading={loading}
      placeholder={placeholder}
      size={size}
      value={value}
      maxTagCount="responsive"
      className={`${styles.select} ${onlyAvatar ? styles.onlyAvatar : ''} ${planInput ? 'ant-form-input-plan' : ''}`}
      style={style}
      suffixIcon={null}
      removeIcon={onlyAvatar ? null : undefined}
      dropdownMatchSelectWidth={dropdownMatchSelectWidth}
      bordered={bordered}
      maxTagTextLength={2}
      maxTagPlaceholder={(omittedValues) => {
        return <span>+{omittedValues.length}</span>;
      }}
      showArrow={false}
      disabled={disabled}
      showSearch
      allowClear
      filterOption={(input, option: any) => option.label.toLowerCase().includes(input.toLowerCase())}
      tagRender={onlyAvatar ? tagRender : undefined}
      onSearch={fetchUsers}
      onChange={(current) => {
        const selected = Array.isArray(current)
          ? options.filter((item) => current.includes(item.value))
          : options.find((item) => item.value === current);

        onChange?.(current, selected);
        fetchUsers();
      }}
      onDropdownVisibleChange={(open) => {
        setDropdownOpen(open);

        if (!open) {
          if (mode !== 'multiple') {
            selectRef.current?.blur();
          }

          fetchUsers();
        }
      }}
    >
      {(filterRender ? filterRender(options) : options).map((item) => (
        <Select.Option key={item.value} label={item.label} value={item.value}>
          <Row wrap={false} align="middle" gutter={[6, 0]}>
            <Col style={{ lineHeight: 1.7 }}>
              {showAvatarPopover ? (
                <UserAvatarPopover
                  user={item.user}
                  size="small"
                  placement={avatarPopoverPlacement}
                  hoverOnly
                  showJobTitle={false}
                />
              ) : (
                <UserAvatar size="small" user={item.user} />
              )}
            </Col>

            <Col flex="auto" className={styles.nameCol}>
              <Text className={colorLabel === 'secondary' ? styles.secondaryLabel : ''} ellipsis>
                {item.label}
              </Text>
            </Col>
          </Row>
        </Select.Option>
      ))}
    </Select>
  );
};

export default SelectUserAvatar;
